import i18n from 'i18n-js';
import moment from 'moment';
import { scale } from '@components/frame';
import { DATA_GRID_IMAGE_COLUMN_WIDTH } from '@v2/pages/common/constants/base.constants';
import { KEYS_TAGS } from '@components/imageHandling/common';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { PartDetailsScreenName } from '@v2/pages/part/details/navigation/config';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { setSecureValue, navigateBack } from '@helpers';
import { trackSearchResultTapped } from '@services/tracking';

interface PartItem {
  partId: number;
  partName?: string;
  imageHash?: string;
  imageDocumentId?: number;
  description?: string;
  [key: string]: any;
}

export const buildNavigationParams = (item: PartItem, levelSubScreen = LevelSubScreen.GENERAL) => ({
  mainScreen: PartDetailsScreenName,
  subScreen: PartDetailsScreen.PART,
  levelSubScreen,
  params: {
    perspective: Perspective.PART,
    partName: item.partName,
    partId: item.partId,
    selectedImageKey: item.imageHash,
    documentId: item.imageDocumentId,
  },
});

export const buildAttachmentData = (item: PartItem, attachImageId: number) => ({
  documentId: attachImageId,
  documentLinks: [
    {
      scopeId: KEYS_TAGS.Part,
      scopeReferenceId: item.partId,
      scopeReferenceName: item.partName,
      isFavorite: false,
      isActivated: true,
      isFavoriteChanged: false,
    },
  ],
  documentTags: [],
});

export const buildRecentlyViewedEntry = (item: PartItem) => ({
  ...item,
  itemType: 'Part',
  itemDescription: item.partName,
  itemMemo: item.description,
  itemId: item.partId,
});

interface HandleOpenParams {
  item: PartItem;
  levelSubScreen?: string;
  isPopupMode?: boolean;
  onItemSelect?: (item: PartItem, type: string) => void;
  attachMode?: boolean;
  attachImageId?: number;
  updateImageMetadata: (data: any) => void;
  navigateToMainDetailsScreen: (params: any) => void;
  recentlyViewed: any[];
  setRecentlyViewed: (items: any[]) => void;
  setSelectedNCFile: (file: any) => void;
  setSelectedItem: (id: number) => void;
}

export const handleOpen = ({
  item,
  levelSubScreen = LevelSubScreen.GENERAL,
  isPopupMode,
  onItemSelect,
  attachMode,
  attachImageId,
  updateImageMetadata,
  navigateToMainDetailsScreen,
  recentlyViewed,
  setRecentlyViewed,
  setSelectedNCFile,
  setSelectedItem,
}: HandleOpenParams) => {
  trackSearchResultTapped('Part');

  const navigationData = {
    partName: item.partName,
    partId: item.partId,
    imageHash: item.imageHash,
    imageDocumentId: item.imageDocumentId,
  };

  if (isPopupMode && onItemSelect) {
    onItemSelect(item, 'Part');
    return;
  }

  if (attachMode && attachImageId) {
    const backendData = buildAttachmentData(navigationData as PartItem, attachImageId);
    updateImageMetadata(backendData);
    navigateBack();
    return;
  }

  const exist = recentlyViewed.find((i: any) => i.itemId === navigationData.partId && i.itemType === 'Part');
  if (!exist) {
    const d = [buildRecentlyViewedEntry(item), ...recentlyViewed];
    setRecentlyViewed(d);
    setSecureValue('recentlyViewed', JSON.stringify(d));
  }
  setSelectedNCFile({});
  setSelectedItem(navigationData.partId);

  navigateToMainDetailsScreen(buildNavigationParams(navigationData as PartItem, levelSubScreen));
};

const makeDateColumn = (field: string, headerKey: string) => ({
  field,
  headerName: i18n.t(headerKey),
  align: 'left' as const,
  width: scale(150),
  type: 'date' as const,
  sortable: true,
  filterable: true,
  valueGetter: (value: string | null) => (value ? new Date(value) : value),
  valueFormatter: (value: Date | null) => (value ? moment(value).format('DD/MM/YYYY') : ''),
});

export const getDataGridColumns = (openFn: (item: any, levelSubScreen?: string) => void, imageRenderFn: (item: any) => any) => [
  {
    field: 'Image',
    headerName: '',
    align: 'center' as const,
    width: scale(DATA_GRID_IMAGE_COLUMN_WIDTH),
    sortable: false,
    filterable: false,
    renderCell: ({ row: item }: { row: any }) => imageRenderFn(item),
  },
  { field: 'partName', headerName: i18n.t('Part Name'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'drawingRevisionPosition', headerName: i18n.t('Drawing + Revision + Position'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'description', headerName: i18n.t('Description'), align: 'left', width: scale(200), sortable: false, filterable: false },
  {
    field: 'linkedNcFiles',
    headerName: i18n.t('Linked NC Files Quantity'),
    align: 'left',
    width: scale(150),
    sortable: false,
    filterable: false,
    valueGetter: (value: any[] | null) => (value ? value.length : null),
  },
  makeDateColumn('modifiedOn', 'Date Modified'),
  makeDateColumn('createdOn', 'Date Created'),
  { field: 'modifiedBy', headerName: i18n.t('Last Modified By'), align: 'left', width: scale(175), sortable: true, filterable: true },
  { field: 'createdBy', headerName: i18n.t('Created By'), align: 'left', width: scale(175), sortable: true, filterable: true },
  { field: 'partSearchName', headerName: i18n.t('Part Search Name'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'drawing', headerName: i18n.t('Drawing'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'revision', headerName: i18n.t('Drawing Revision'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'position', headerName: i18n.t('Drawing Position'), align: 'left', width: scale(150), sortable: true, filterable: true },
];
